import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Skeleton } from "./ui/skeleton";
import { AlertCircle, ChevronRight, Clock, XCircle } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Visual mapping per canonical business_status
const STATUS_VIZ = {
  draft:      { color: "#94a3b8", label: "Draft" },
  signed:     { color: "#3b82f6", label: "Semnat" },
  submitted:  { color: "#6366f1", label: "Trimis" },
  processing: { color: "#f59e0b", label: "În procesare" },
  validated:  { color: "#10b981", label: "Validat" },
  active:     { color: "#16a34a", label: "Activ" },
  terminated: { color: "#ef4444", label: "Terminat" },
  cancelled:  { color: "#f43f5e", label: "Anulat" },
  rejected:   { color: "#be123c", label: "Respins" },
};

// Build a CSS conic-gradient from segments
const buildConic = (segments, total) => {
  if (total === 0) return "conic-gradient(#e2e8f0 0 360deg)";
  let acc = 0;
  const stops = segments.map(({ status, count }) => {
    const start = (acc / total) * 360;
    acc += count;
    const end = (acc / total) * 360;
    const c = STATUS_VIZ[status]?.color || "#cbd5e1";
    return `${c} ${start}deg ${end}deg`;
  });
  return `conic-gradient(${stops.join(", ")})`;
};

const BusinessStatusDistribution = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/dashboard/business-status-distribution`);
        setData(r.data);
      } catch (e) {
        setError(e.response?.data?.detail || "Eroare la încărcare");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="bs-dist-loading">
        <Card className="lg:col-span-2"><CardContent className="pt-6"><Skeleton className="h-48 w-full" /></CardContent></Card>
        <div className="space-y-6">
          <Card><CardContent className="pt-6"><Skeleton className="h-24 w-full" /></CardContent></Card>
          <Card><CardContent className="pt-6"><Skeleton className="h-24 w-full" /></CardContent></Card>
        </div>
      </div>
    );
  }
  if (error) return null;
  if (!data) return null;

  const segments = data.distribution.filter((d) => d.count > 0);
  const total = data.total || 0;
  const conic = buildConic(segments, total);
  const cutoffDate = data.terminated_cutoff_date
    ? new Date(data.terminated_cutoff_date).toLocaleDateString("ro-RO")
    : "—";

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="bs-dist-widget">
      {/* Pie chart + legend */}
      <Card className="lg:col-span-2 hover:shadow-md transition-shadow">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Distribuție Business Status</CardTitle>
          <p className="text-xs text-slate-500">
            {total.toLocaleString("ro-RO")} contracte în scope-ul tău · click pe segment pentru drill-down
          </p>
        </CardHeader>
        <CardContent>
          {total === 0 ? (
            <p className="text-sm text-slate-400 py-8 text-center">
              Niciun contract în scope-ul tău încă.
            </p>
          ) : (
            <div className="flex flex-col md:flex-row items-center gap-6">
              {/* Pie */}
              <div
                className="relative h-44 w-44 rounded-full flex items-center justify-center shrink-0"
                style={{ background: conic }}
              >
                <div className="h-28 w-28 rounded-full bg-white flex flex-col items-center justify-center shadow-inner">
                  <span className="text-xs text-slate-500">Total</span>
                  <span className="text-2xl font-bold text-slate-900" data-testid="bs-dist-total">
                    {total.toLocaleString("ro-RO")}
                  </span>
                </div>
              </div>
              {/* Legend / drill-down list */}
              <div className="flex-1 w-full space-y-1.5">
                {segments
                  .slice()
                  .sort((a, b) => b.count - a.count)
                  .map(({ status, count }) => {
                    const viz = STATUS_VIZ[status] || { color: "#cbd5e1", label: status };
                    const pct = total ? ((count / total) * 100).toFixed(1) : 0;
                    return (
                      <Link
                        key={status}
                        to={`/sales?business_status=${status}`}
                        className="flex items-center justify-between gap-3 px-2 py-1.5 rounded hover:bg-slate-50 group"
                        data-testid={`bs-dist-row-${status}`}
                      >
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          <span
                            className="h-3 w-3 rounded-sm shrink-0"
                            style={{ backgroundColor: viz.color }}
                          />
                          <span className="text-sm text-slate-700 truncate">{viz.label}</span>
                          <span className="text-xs text-slate-400 shrink-0">({pct}%)</span>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <span className="text-sm font-medium text-slate-900 tabular-nums">
                            {count.toLocaleString("ro-RO")}
                          </span>
                          <ChevronRight className="h-3.5 w-3.5 text-slate-300 group-hover:text-slate-600 transition-colors" />
                        </div>
                      </Link>
                    );
                  })}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* KPI cards stacked */}
      <div className="space-y-6">
        <Link to="/sales?business_status=validated" data-testid="kpi-validated-not-active-link">
          <Card className="border-l-4 border-l-emerald-500 hover:shadow-lg transition-shadow cursor-pointer">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-xs font-medium text-slate-500 flex items-center gap-1.5">
                    <Clock className="h-3.5 w-3.5" />
                    Validat dar nu Activ
                  </p>
                  <p className="text-3xl font-bold text-slate-900 mt-1 tabular-nums" data-testid="kpi-validated-not-active">
                    {data.validated_not_active.toLocaleString("ro-RO")}
                  </p>
                  <p className="text-xs text-slate-400 mt-1">
                    Easy validated, așteaptă activare SF
                  </p>
                </div>
                {data.validated_not_active > 0 && (
                  <AlertCircle className="h-6 w-6 text-emerald-500 shrink-0" />
                )}
              </div>
            </CardContent>
          </Card>
        </Link>
        <Link to="/sales?business_status=terminated" data-testid="kpi-terminated-30d-link">
          <Card className="border-l-4 border-l-red-500 hover:shadow-lg transition-shadow cursor-pointer">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-xs font-medium text-slate-500 flex items-center gap-1.5">
                    <XCircle className="h-3.5 w-3.5" />
                    Terminate în ultimele 30 zile
                  </p>
                  <p className="text-3xl font-bold text-slate-900 mt-1 tabular-nums" data-testid="kpi-terminated-30d">
                    {data.terminated_last_30d.toLocaleString("ro-RO")}
                  </p>
                  <p className="text-xs text-slate-400 mt-1">de la {cutoffDate}</p>
                </div>
                {data.terminated_last_30d > 0 && (
                  <AlertCircle className="h-6 w-6 text-red-500 shrink-0" />
                )}
              </div>
            </CardContent>
          </Card>
        </Link>
      </div>
    </div>
  );
};

export default BusinessStatusDistribution;
